import React, { useState, useEffect, useRef } from 'react'
import { Form, useFormik, FormikProvider } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { user } from 'ampersand-app'
import moment from 'moment'
import config from '@/config'
import Panel from '@/components/layouts/panel/index'
import PanelHeader from '@/components/layouts/panel/panel-header'
import PanelContent from '@/components/layouts/panel/panel-content'
import lang from '@/lang'
import {
  CurrencyInput,
  DateInput,
  NumberInput,
  SelectInput,
  TextInput,
} from '@/modules/base/components/inputs/index'
import ButtonIconSquare, { VARIANTS } from '@/modules/base/components/buttons/icon-square/button-icon-square'
import PanelFooter from '@/components/layouts/panel/panel-footer'
import IconCheck from '@assets/images/icons/check.svg'
import EconomicAssetTablePreview from './economicAsset-table-preview'
import { EconomicAssetService } from '@/modules/economicAsset/economicAssetService'
import * as Yup from 'yup'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import ExpenseAndRevenueDrawer from '@/modules/expense-revenue/components/drawer/detail-drawer'
import ExpenseAndRevenueDetailView from '@/modules/expense-revenue/components/drawer/detail-view'
import Loader from '@/modules/base/components/loader/loader'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { BookingService } from '@/modules/banking/services'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import PropTypes from 'prop-types'
import { ACCOUNT_CATEGORY, DEPRECATION_METHODS, getEconomicAssetTypLabel } from '../../constants'
import clsx from 'clsx'
import ItemSelection from '@/modules/base/components/item-selection/item-selection'
import { refreshList } from '@/redux/ducks/economicAsset/list'
import { SquareIconButton } from '@/modules/base/components/buttons'
import IconTrashcan from '@assets/images/icons/trashcan.svg'
import EconomicAssetBookingModal from './booking-modal/economic-asset-booking-modal'
import { assignmentTypes } from '@/modules/base/util/constants'
import Checkbox from '@/modules/base/components/inputs/checkbox/checkbox'
import GWGHelper from '@/modules/receipt/_helpers/GWGHelper'
import { orderBy, sumBy, debounce } from 'lodash'
import { CHART_OF_ACCOUNT_SKR_TYPE } from '@/modules/settings/components/tax-settings/chart-of-accounts/constants'
import ErrorCodes from '@/enums/error-codes.enum'
const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
  },
  contentForm: {
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    height: '100%',
  },
  loader: {
    height: '100%',
    background: 'rgba(255, 255, 255, 0.5)',
  },
  dateRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 5,
    width: '100%',
  },
  mainContainer: {
    width: 486,
    border: theme.border,
  },
  panelHeader: {
    backgroundColor: theme.backgroundColor,
    height: 85,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    paddingRight: 15,
  },
  headerLabel: {
    order: -1,
    paddingLeft: 30,
    fontSize: 20,
    color: theme.black,
    whiteSpace: 'nowrap',
    display: 'flex',
    maxWidth: 394,
    flexDirection: 'column',
  },
  panelFooterWrapper: {
    backgroundColor: theme.backgroundColor,
    display: 'flex',
    justifyContent: 'end',
    '& > div': {
      margin: 20,
    },
  },
  content: {
    margin: 10,
  },
  input: {
    padding: 5,
    '& label': {
      color: theme.text,
    },
  },
  inputMethod: {
    padding: '15px 5px 5px 5px',
    '& label': {
      color: theme.text,
    },
  },
  depreciationMethodInput: {
    padding: 5,
    '& label': {
      color: theme.text,
    },
  },
  dateInput: {
    width: '50%',
    '& label': {
      color: `${theme.black} !important`,
    },
  },
  economicInput: {
    paddingLeft: 30,
    width: '50%',
    '& label': {
      color: theme.text,
    },
  },
  footerButtons: {
    display: 'flex',
    width: '100%',
    justifyContent: 'end',
  },
  expenseButton: {
    marginRight: 30,
  },
  tooltipWrapper: {
    height: 'fit-content',
    color: theme.black,
  },
  tooltipTitle: {
    paddingBottom: '20px',
    textAlign: 'left',
    fontSize: theme.fontSizes.default,
    lineHeight: '20px',
    color: theme.black,
    fontWeight: theme.fontWeights.bold,
  },
  tooltipSubtitle: {
    paddingTop: 10,
    textAlign: 'left',
    fontSize: theme.fontSizes.default,
    lineHeight: '20px',
    color: theme.black,
    fontWeight: theme.fontWeights.semiBold,
  },
  tooltipContent: {
    fontSize: theme.fontSizes.smaller,
  },
  label: {
    display: 'block',
    fontSize: 13,
    marginBottom: 10,
  },
  selection: {
    width: 'auto',
  },
  bold: {
    fontWeight: theme.fontWeights.semiBold,
  },
  spanPadding: {
    paddingLeft: '1ch',
  },
  footerButtonsContent: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  checkbox: {
    display: 'flex',
    gap: 10,
    flexDirection: 'row',
  },
}))

const EconomicAssetDetailView = (props) => {
  const dispatch = useDispatch()
  const liveAndRecoverPeriod = useRef([])
  const prevDescriptionRef = useRef()
  const [calculateData, setCalculateData] = useState([])
  const [method, setMethod] = useState('linear')
  const [costOfPurchase, setCostOfPurchase] = useState()
  const [economicLifeForTable, setEconomicLifeForTable] = useState()
  const [economicAssetData, setEconomicAssetData] = useState([])
  const [openEditDraw, setOpenEditDraw] = useState(false)
  const [expenseId, setExpenseId] = useState()
  const [revenueId, setRevenueId] = useState()
  const [chartOfAccounts, setChartOfAccounts] = useState()
  const [periodType, setPeriodType] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [calculationIsLoading, setCalculationIsLoading] = useState(false)
  const [openBookingDrawer, setOpenBookingDrawer] = useState(false)
  const [isSpecialDepreciation, setIsSpecialDepreciation] = useState(false)
  const [specialDepreciationData, setSpecialDepreciationData] = useState({})
  const [isGWG, setIsGWG] = useState(false)
  const [isElectricVehicleSpecial, setIsElectricVehicleSpecial] = useState(false)
  const [drawerType, setDrawerType] = useState(null)
  const [possibleDepreciationMethods, setPossibleDepreciationMethods] = useState([])
  const classes = useStyles(props)
  const { onClose, economicAssetId, isExpense, expenseFinanceApiId, accountCategoryEconomicAssetData, expenseDate, investmentAccounts, onDelete, allowDelete, className } = props
  const { dateFormat: { api: apiDateFormat } } = config
  const gwgAccountName = 'Geringwertiges Wirtschaftsgut (GWG)'
  const currentSkrType = useSelector(state => state.tenant.settings.currentSkrType)
  const isSmallBusiness = user.isSmallBusiness
  const deprecationMethodsDataOptions = calculateData
    .filter(item => possibleDepreciationMethods.some(m => m.key === item.depreciationMethod))
    .map(item => {
    const possibleDepreciationMethod = possibleDepreciationMethods.find(method => method.key === item.depreciationMethod)
    const label = possibleDepreciationMethod ? possibleDepreciationMethod?.shortLabel : lang.economicAsset.type[item.depreciationMethod] || item.depreciationMethod
    return {
      label,
      value: item.depreciationMethod,
    }
  })

  const setNextInventoryNumber = async() => {
    const { data } = await EconomicAssetService.getAll()
    const existingNumbers = new Set(data.map(item => item.number))
    let nextNumber = 1000
    while (existingNumbers.has(nextNumber.toString())) {
      nextNumber++
    }
    formik.setFieldValue(
      'number', nextNumber,
    )
  }

  const getMinEconomicLife = () => {
    return isGWG ? 0 : 1
  }

  const formik = useFormik({
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    initialValues: {
      costOfPurchase: 0,
      dateOfPurchase: moment().format(apiDateFormat),
      depreciationMethod: DEPRECATION_METHODS.linear,
      classLiveAndPeriodId: '',
    },
    validationSchema: Yup.object({
      costOfPurchase: Yup.number()
        .required(lang.emptyRequiredFieldErrorMessage)
        .when('depreciationMethod', {
        is: DEPRECATION_METHODS.lowValueAsset,
        then: (schema) => schema
          .moreThan(250, lang.economicAsset.errors.costOfPurchaseTooLowErrorMessage)
          .max(1000.01, lang.economicAsset.errors.costOfPurchaseTooHighErrorMessage),
        otherwise: (schema) => schema.moreThan(0, lang.economicAsset.errors.costOfPurchaseDefaultErrorMessage),
      }),
      depreciationMethod: Yup.string().required(lang.emptyRequiredFieldErrorMessage),
      classLiveAndPeriodId: Yup.string().required(lang.emptyRequiredFieldErrorMessage),
      description: Yup.string().required(lang.emptyRequiredFieldErrorMessage),
      accountNo: Yup.string().required(lang.emptyRequiredFieldErrorMessage),
      number: Yup.string().required(lang.emptyRequiredFieldErrorMessage),
      dateOfPurchase: Yup.date().required(lang.emptyRequiredFieldErrorMessage),
      economicLife: Yup.number()
        .transform((v) => (v === '' || v == null) ? undefined : Number(v))
        .required(lang.emptyRequiredFieldErrorMessage)
        .min(getMinEconomicLife(), lang.economicAsset.view.errorEconomicLife(getMinEconomicLife())),
    }),
  })

  const onSubmit = async() => {
    try {
      await EconomicAssetService.add(formik.values)
      toast.showToast({ message: lang.economicAsset.toast.success })
      onClose()
      dispatch(refreshList())
    } catch (error) {
      console.log(error)
      if (error.body.meta[0]?.BadRequest?.[0]?.message.includes(lang.economicAsset.toast.finApiError)) {
        toast.showToast({ type: 'error', message: lang.economicAsset.toast.idError })
        return
      }
      if (error.body.meta[0]?.[ErrorCodes.NOT_ALLOWED][0]?.message.includes(lang.economicAsset.toast.finApiError2)) {
        toast.showToast({ type: 'error', message: lang.economicAsset.toast.idError })
        return
      }
      toast.showToast({ type: 'error', message: lang.economicAsset.toast.error })

    }
  }

  const allDepreciationMethods = useRef([])
  const getCalculateDataRef = useRef(null)
  const debouncedGetCalculateData = useRef(debounce(() => getCalculateDataRef.current?.(), 500)).current

  const isEligibleForElectricVehicleSpecial = (accountNo, currentSkrType, dateOfPurchase) => {
    const eligibleSkr03Accounts = [320, 350, 380]
    const eligibleSkr04Accounts = [520, 540, 560]
    const eligibleAccounts = currentSkrType === CHART_OF_ACCOUNT_SKR_TYPE.skr03 ? eligibleSkr03Accounts : eligibleSkr04Accounts
    return eligibleAccounts.includes(accountNo) && moment(dateOfPurchase).isSameOrAfter('2025-07-01') && moment(dateOfPurchase).isSameOrBefore('2027-12-31')
  }

  const isGWGAccount = (accountNumber) =>
    (accountNumber === 480 && currentSkrType === CHART_OF_ACCOUNT_SKR_TYPE.skr03)
    || (accountNumber === 670 && currentSkrType === CHART_OF_ACCOUNT_SKR_TYPE.skr04)

  const getCurrentClassLife = () =>
    liveAndRecoverPeriod.current?.find(item => item.id === formik.values.classLiveAndPeriodId)?.classLife

  const applyDepreciationMethodChange = (methodKey) => {
    const economicLife = methodKey === DEPRECATION_METHODS.electricVehicleSpecial ? 6 : getCurrentClassLife()
    formik.setFieldValue('economicLife', economicLife)
    formik.setFieldValue('depreciationMethod', methodKey)
    setIsElectricVehicleSpecial(methodKey === DEPRECATION_METHODS.electricVehicleSpecial)
  }

  const applyElectricVehicleEligibility = (isEligible) => {
    if (isEligible) {
      setIsElectricVehicleSpecial(true)
      setPossibleDepreciationMethods(allDepreciationMethods.current)
    } else {
      setIsElectricVehicleSpecial(false)
      setPossibleDepreciationMethods(allDepreciationMethods.current.filter(m => m.key !== DEPRECATION_METHODS.electricVehicleSpecial))
      setMethod(DEPRECATION_METHODS.linear)
      formik.setFieldValue('depreciationMethod', DEPRECATION_METHODS.linear)
    }
  }

  const getLiveAndRecoverPeriod = async() => {
    const { data: liveAndRecoverPeriodData, meta: { depreciationMethods } } = await EconomicAssetService.getClassesLiveAndRecoverPeriod()
    liveAndRecoverPeriod.current = liveAndRecoverPeriodData
    allDepreciationMethods.current = depreciationMethods
    setPossibleDepreciationMethods(depreciationMethods)
  }

  const onDeleteClick = () => {
    onDelete && onDelete(economicAssetId)
  }

  const openExpenseRevenueDrawer = (type) => {
    setDrawerType(type)
    setOpenEditDraw(true)
  }

  const closeExpenseEditDrawer = () => {
    setOpenEditDraw(false)
    setDrawerType(null)
  }

  const getEconomicAsset = async() => {
    setIsLoading(true)
    const { data } = await EconomicAssetService.getEconomicAsset(economicAssetId)
    const selectedAccount = liveAndRecoverPeriod.current?.find(item => item.debitBookkeepingAccount.id === data.debitBookkeepingAccountId)
    const setDepreciationMethod = getEconomicAssetTypLabel(data.schedulesOfDepreciation[0].depreciationMethod)
    setMethod(data.schedulesOfDepreciation[0].depreciationMethod)

    const allAnnualDepreciationRecords = data.schedulesOfDepreciation.flatMap(item => item.annualDepreciationRecords)
    let sortedAllAnnualDepreciationRecords = orderBy(allAnnualDepreciationRecords, ['year'], ['asc'])
    const closingAnnualDepreciationRecord = sortedAllAnnualDepreciationRecords.find(item => item.endingBookValue === 0)

    if (closingAnnualDepreciationRecord) {
      const closingYearAnnualDepreciationRecords = sortedAllAnnualDepreciationRecords.filter(item => item.year === closingAnnualDepreciationRecord.year)
      const depreciationAmount = sumBy(closingYearAnnualDepreciationRecords, record => record.depreciationAmount)

      sortedAllAnnualDepreciationRecords = [
        ...sortedAllAnnualDepreciationRecords.filter(item => item.year !== closingAnnualDepreciationRecord.year),
        { ...closingAnnualDepreciationRecord, depreciationAmount },
      ]
    }

    setEconomicAssetData(sortedAllAnnualDepreciationRecords)
    setExpenseId(data.expenseId)
    setRevenueId(data?.expenseIncomeId)
    formik.setValues({
      description: data.description,
      number: data.number,
      classLiveAndPeriodId: data.classLiveAndRecoverPeriod.description,
      economicLife: data.economicLife,
      accountNo: selectedAccount.debitBookkeepingAccount.number,
      costOfPurchase: formatCurrency(data.costOfPurchase),
      dateOfPurchase: data.dateOfPurchase,
      depreciationMethod: setDepreciationMethod,
    })
    setPeriodType(data.classLiveAndRecoverPeriod.description)
    setCostOfPurchase(data.costOfPurchase)
    setEconomicLifeForTable(data.economicLife)
    setIsSpecialDepreciation(!!data.specialDepreciationDoneOn)
    setSpecialDepreciationData({
      specialDepreciation: data.specialDepreciation,
      specialDepreciationComment: data.specialDepreciationComment,
      specialDepreciationDoneOn: data.specialDepreciationDoneOn,
    })
    setIsLoading(false)
  }

  const getEconomicAssetFromExpense = () => {
    const allAccounts = liveAndRecoverPeriod.current?.filter(item => item.debitBookkeepingAccount.id === accountCategoryEconomicAssetData.bookkeepingAccountId)
    const selectedAccount = liveAndRecoverPeriod.current?.find(item => item.debitBookkeepingAccount.id === accountCategoryEconomicAssetData.bookkeepingAccountId)
    if (!selectedAccount) return

    const commonValues = {
      accountNo: accountCategoryEconomicAssetData.bookkeepingAccountNo,
      costOfPurchase: isSmallBusiness ? accountCategoryEconomicAssetData.totalGross : accountCategoryEconomicAssetData.totalNet,
      dateOfPurchase: expenseDate,
      debitBookkeepingAccountId: accountCategoryEconomicAssetData.bookkeepingAccountId,
      classLiveAndPeriodId: selectedAccount.id,
      economicLife: selectedAccount.classLife,
      expenseId: expenseFinanceApiId,
    }

    if (isGWGAccount(selectedAccount.debitBookkeepingAccount.number)) {
      const gwgAccount = allAccounts.find(item => item.description === gwgAccountName)
      setIsGWG(true)
      formik.setValues({
        ...commonValues,
        depreciationMethod: DEPRECATION_METHODS.lowValueAsset,
        classLiveAndPeriodId: gwgAccount.id,
        economicLife: gwgAccount.classLife,
      })
    } else {
      formik.setValues({
        ...commonValues,
        depreciationMethod: DEPRECATION_METHODS.linear,
      })
    }
  }

  const updateTablePreviewData = () => {
      setCostOfPurchase(formik.values.costOfPurchase)
      setEconomicLifeForTable(formik.values.economicLife)
      debouncedGetCalculateData()
  }

  useEffect(() => {
    getLiveAndRecoverPeriod().then(() => {
      if (economicAssetId) {
        return getEconomicAsset()
      } else if (expenseFinanceApiId) {
        getEconomicAssetFromExpense()
        return setNextInventoryNumber()
      } else {
        return Promise.all([
          setNextInventoryNumber(),
          fetch(),
        ])
      }
    }).then(() => {
      setIsLoading(false)
    })
  }, [])

  useEffect(() => {
    if (!economicAssetId && formik.isValid) {
      updateTablePreviewData()
    }
  }, [
    formik.values.costOfPurchase,
    formik.values.dateOfPurchase,
    formik.values.economicLife,
    formik.values.classLiveAndPeriodId,
    formik.values.debitBookkeepingAccountId  
  ])

  useEffect(() => {
    return () => debouncedGetCalculateData.cancel()
  }, [])

  useEffect(() => {
    if (!economicAssetId && !prevDescriptionRef.current && formik.values.description) {
      updateTablePreviewData()
    }
    prevDescriptionRef.current = formik.values.description
  }, [formik.values.description])

  const chartOfAccountsOptions = () => {
    if (!economicAssetId) {
      if (!chartOfAccounts && !expenseFinanceApiId) {
        return []
      }
      if (investmentAccounts || chartOfAccounts) {
        const sortedAccounts = Object.entries(investmentAccounts || chartOfAccounts)
          .map(([key, value]) => ({
            label: `${value.accountNo} - ${value.accountDescriptionCustom}`,
            value: value.accountNo,
          }))
          .reverse()
        return sortedAccounts
      }

    }
    return []
  }

  const fetch = async() => {
    const { data: { chartsOfAccount: { accounts } } } = await BookingService.chartOfAccounts.getList({ accountCategory: ACCOUNT_CATEGORY.investmentAccounts })
    setChartOfAccounts(accounts)
  }

  const getCalculateData = async() => {
    if (method === DEPRECATION_METHODS.lowValueAsset) return
    if (!formik.isValid || !formik.values.classLiveAndPeriodId) return

    setCalculationIsLoading(true)

    if (isEligibleForElectricVehicleSpecial(formik.values.accountNo, currentSkrType, formik.values.dateOfPurchase)) {
      setPossibleDepreciationMethods(allDepreciationMethods.current)
    }
    else
    {
      setMethod(DEPRECATION_METHODS.linear)
      formik.setFieldValue('depreciationMethod', DEPRECATION_METHODS.linear)
      setPossibleDepreciationMethods(allDepreciationMethods.current.filter(method => method.key !== DEPRECATION_METHODS.electricVehicleSpecial))      
    }    
    try {
      const { data: calculateData } = await EconomicAssetService.addCalculate(formik.values)
      setCalculateData(calculateData)
    } catch (error) {
      toast.showToast({ type: 'error', message: lang.economicAsset.toast.errorCalculate })
    } finally {
      setCalculationIsLoading(false)
    }
  }

  getCalculateDataRef.current = getCalculateData

  useEffect(() => {
    if (!economicAssetId) {
      setMethod(formik.values.depreciationMethod)
    }
  }, [formik.values.depreciationMethod])

  const getClassesLiveAndRecoverPeriodOptions = () => {
    if (!economicAssetId) {
      if (!liveAndRecoverPeriod.current) {
        return []
      }

      const groupedOptions = liveAndRecoverPeriod.current?.reduce((result, option) => {
        const mainCategory = option.mainCategory
        if (!result[mainCategory]) {
          result[mainCategory] = []
        }

        result[mainCategory].push({ label: option.description, value: option.id })
        return result
      }, {})
      const groupedOptionsArray = Object.entries(groupedOptions).map(([mainCategory, options]) => ({
        label: mainCategory,
        options: options.map(option => ({ label: option.label, value: option.value })),
      }))
      return groupedOptionsArray
    } else return []
  }

  const handleDepreciationMethodChange = (methodKey) => {
    if (!economicAssetId) {
      setMethod(methodKey)
      applyDepreciationMethodChange(methodKey)
    }
  }

  const handleAssetTypeChange = (options, setFieldValue) => {
    if (!economicAssetId) {
      const selectedObject = liveAndRecoverPeriod.current?.find(item => item.id === options.value)
      setFieldValue('classLiveAndPeriodId', options.value)

      if (!expenseFinanceApiId) {
        setFieldValue('accountNo', selectedObject.debitBookkeepingAccount.number)
        setFieldValue('debitBookkeepingAccountId', selectedObject.debitBookkeepingAccount.id)
        if (isGWGAccount(selectedObject.debitBookkeepingAccount.number)) {
          setIsGWG(true)
          setFieldValue('depreciationMethod', DEPRECATION_METHODS.lowValueAsset)
          setFieldValue('economicLife', 0)
        } else {
          setIsGWG(false)
          setFieldValue('economicLife', selectedObject.classLife)

          const evEligible = isEligibleForElectricVehicleSpecial(
            selectedObject.debitBookkeepingAccount.number, currentSkrType, formik.values.dateOfPurchase
          )
          setFieldValue('depreciationMethod', DEPRECATION_METHODS.linear)
          applyElectricVehicleEligibility(evEligible)
        }
      }
    }
  }

  const handleMethod = (value) => {
    applyDepreciationMethodChange(value)
  }

  const handleAccountNoChange = (options, setFieldValue) => {
    if (!economicAssetId) {
      const selectedObject = chartOfAccounts?.find(item => item.accountNo === options.value)
      setFieldValue('debitBookkeepingAccountId', selectedObject.id)
      
      if (isGWGAccount(selectedObject.number)) {
        setIsGWG(true)
        setFieldValue('depreciationMethod', DEPRECATION_METHODS.lowValueAsset)
        setFieldValue('economicLife', 0)
      } else {
        setIsGWG(false)
        const evEligible = isEligibleForElectricVehicleSpecial(selectedObject.number, currentSkrType, formik.values.dateOfPurchase)
        const economicLife = evEligible && method === DEPRECATION_METHODS.electricVehicleSpecial ? 6 : getCurrentClassLife()
        setFieldValue('depreciationMethod', DEPRECATION_METHODS.linear)
        setFieldValue('economicLife', economicLife)
        applyElectricVehicleEligibility(evEligible)
      }
    }
  }

  const tooltipContent = (<div className={classes.tooltipWrapper}>
    <div className={classes.tooltipTitle}>{lang.economicAsset.view.tooltip.title}</div>
    <div className={classes.tooltipSubtitle}>{lang.economicAsset.view.tooltip.subTitle1}</div>
    <div className={classes.tooltipContent}>{lang.economicAsset.view.tooltip.description1}</div>
    <div className={classes.tooltipSubtitle}>{lang.economicAsset.view.tooltip.subTitle2}</div>
    <div className={classes.tooltipContent}>{lang.economicAsset.view.tooltip.description2}</div>
    <div className={classes.tooltipSubtitle}>{lang.economicAsset.view.tooltip.subTitle3}</div>
    <div className={classes.tooltipRow}>
      <span className={clsx(classes.tooltipContent, classes.bold)}>{lang.economicAsset.view.tooltip.subTitle4}</span>
      <span className={clsx(classes.tooltipContent, classes.spanPadding)}>{lang.economicAsset.view.tooltip.subTitle5}</span>
    </div>
    <div className={classes.tooltipContent}>{lang.economicAsset.view.tooltip.description3}</div>
    <div className={classes.tooltipContent}>{lang.economicAsset.view.tooltip.description4}</div>
  </div>
  )

  const tooltipButton = (<div className={classes.tooltipWrapper}>
    <div className={classes.tooltipTitle}>{lang.economicAsset.view.tooltip.buttonTitle}</div>
    <div className={classes.tooltipContent}>{lang.economicAsset.view.tooltip.emptyStateDescription}</div>
  </div>
  )

  const content = (
    <>
      <PanelHeader onClose={onClose} className={classes.panelHeader} displayClose>
        <div className={classes.headerLabel}>
          <span>{lang.economicAsset.drawer.title}</span>
        </div>
      </PanelHeader>
      <FormikProvider value={formik} >
        <Form className={classes.contentForm}>
          <PanelContent className={classes.content}>
            <TextInput
              name="description"
              label={lang.economicAsset.view.description}
              useFormik
              className={classes.input}
              readOnly={economicAssetId}
              placeholder={lang.economicAsset.view.placeholderDescription}
            />
            {economicAssetId
              ? <TextInput
                label={lang.economicAsset.view.classLiveAndPeriod}
                name="classLiveAndPeriodId"
                className={classes.input}
                readOnly={economicAssetId}
              />
              : <SelectInput
                name="classLiveAndPeriodId"
                label={lang.economicAsset.view.classLiveAndPeriod}
                notAsync
                allowCreate={false}
                options={getClassesLiveAndRecoverPeriodOptions()}
                useFormik
                handleChange={(options) => {
                  handleAssetTypeChange(options, formik.setFieldValue)
                }}
                readOnly={economicAssetId || expenseFinanceApiId}
                className={classes.input}
                customSelectOptions={{
                  placeholder: lang.economicAsset.view.placeholderSelectInput,
                }}
              />
            }
            <div className={classes.dateRow}>
              <DateInput
                name="dateOfPurchase"
                label={lang.economicAsset.view.dateOfPurchase}
                className={classes.dateInput}
                useFormik
                readOnly={economicAssetId}
              />
              <div className={classes.economicInput}>
                <NumberInput
                  useFormik={true}
                  label={lang.economicAsset.view.economicLife}
                  name="economicLife"
                  stepSize={1}
                  precision={0}
                  placeholder={4}
                  min={getMinEconomicLife()}
                  showButtons
                  defaultValue={formik?.values?.economicLife}
                  disabled={economicAssetId || isGWG || (isElectricVehicleSpecial && method === DEPRECATION_METHODS.electricVehicleSpecial)}
                />
              </div>
            </div>

            {economicAssetId
              ? <TextInput
                label={isSmallBusiness ? lang.economicAsset.view.costOfPurchaseSmallBusiness : lang.economicAsset.view.costOfPurchase}
                name="costOfPurchase"
                className={classes.input}
                readOnly={true}
              />
              : <CurrencyInput
                name="costOfPurchase"
                label={isSmallBusiness ? lang.economicAsset.view.costOfPurchaseSmallBusiness : lang.economicAsset.view.costOfPurchase}
                useFormik
                customClass={classes.input}
                readOnly={economicAssetId || expenseFinanceApiId}
              />
            }
            {isGWG
              ? <GWGHelper positions={ [
                {
                  totalNet: formik.values.costOfPurchase,
                  bookkeepingAccountNo: formik.values.accountNo,
                  bookkeepingAccountId: formik.values.debitBookkeepingAccountId,
                }]} />
              : null
            }

            {economicAssetId
              ? <TextInput
                label={lang.economicAsset.view.accountNo}
                name="accountNo"
                className={classes.input}
                readOnly={true}
              />
              : <SelectInput
                name="accountNo"
                label={lang.economicAsset.view.accountNo}
                notAsync
                allowCreate={false}
                options={chartOfAccountsOptions()}
                useFormik
                className={classes.input}
                handleChange={(options) => handleAccountNoChange(options, formik.setFieldValue)}
                readOnly={economicAssetId || expenseFinanceApiId}
                upwards={true}
                customSelectOptions={{
                  placeholder: lang.economicAsset.view.placeholderSelectInput,
                }}
              />

            }
            <TextInput
              name="number"
              label={lang.economicAsset.view.inventoryNumber}
              useFormik
              placeholder={lang.economicAsset.view.placeholderNumber}
              className={classes.input}
              readOnly={economicAssetId}
            />
            {
              economicAssetId
                ? <TextInput
                  label={lang.economicAsset.view.depreciationMethod}
                  name="depreciationMethod"
                  className={classes.depreciationMethodInput}
                  readOnly={economicAssetId}
                />
                : (deprecationMethodsDataOptions.length > 0 || isGWG)
                  ? <div className={classes.inputMethod}>
                    <label className={classes.label}>{lang.economicAsset.view.placeholderMethod}</label>
                    {!isGWG
                      ? <RichTooltip content={tooltipContent} width={360} placement={'top'}>
                        <div className={classes.selection}>
                          <ItemSelection
                            allItems={deprecationMethodsDataOptions}
                            selectedItem={method}
                            onItemClick={handleMethod} />
                        </div>
                      </RichTooltip>
                      : <div className={classes.checkbox}>
                        <Checkbox
                          checked={isGWG}
                          disabled={isGWG}
                        >
                        </Checkbox>
                        <label className={classes.label}>{lang.economicAsset.view.directDeprecationMethod}</label>
                      </div>
                    }
                  </div>
                  : null
            }
          </PanelContent>
          <PanelFooter className={classes.panelFooterWrapper}>
            <div className={classes.footerButtons}>
              {economicAssetId && !isSpecialDepreciation && (
                <div className={classes.footerButtonsContent}>
                  <SquareIconButton icon={IconTrashcan} onClick={() => onDeleteClick()} tooltip={lang.deleteButtonLabel} disabled={!allowDelete} />
                  <ButtonIconSquare
                    className={classes.expenseButton}
                    variant={VARIANTS.outline}
                    label={lang.economicAsset.bookingModal.buttonTitle}
                    onClick={() => setOpenBookingDrawer(true)}
                    disabled={isExpense || (method === DEPRECATION_METHODS.lowValueAsset)}
                  />
                </div>
              )}
              {economicAssetId && (
                <div className={classes.footerButtonsContent}>
                  {revenueId
                    ? <ButtonIconSquare
                      className={classes.expenseButton}
                      variant={VARIANTS.outline}
                      label={lang.economicAsset.preview.revenueButtonTitle}
                      onClick={() => openExpenseRevenueDrawer(assignmentTypes.REVENUE)}
                      disabled={isExpense}
                    />
                    : null
                  }
                  <ButtonIconSquare
                    className={classes.expenseButton}
                    variant={VARIANTS.outline}
                    label={lang.economicAsset.preview.expenseButtonTitle}
                    onClick={() => openExpenseRevenueDrawer(assignmentTypes.EXPENSE)}
                    disabled={isExpense}
                  />
                </div>
              )}
              {
                economicAssetId
                  ? null
                  : formik?.isValid && (!isGWG || (formik.values.costOfPurchase > 250 && formik.values.costOfPurchase <= 1000.01))
                    ? <ButtonIconSquare
                      icon={IconCheck}
                      label={lang.saveButtonLabel}
                      disabled={!formik?.isValid}
                      onClick={onSubmit}
                    />
                    : <RichTooltip content={tooltipButton} width={260} placement={'top'}>
                      <div>
                        <ButtonIconSquare
                          icon={IconCheck}
                          label={lang.saveButtonLabel}
                          disabled={!formik?.isValid || (isGWG && (formik.values.costOfPurchase <= 250 || formik.values.costOfPurchase > 1000.01))}
                          onClick={onSubmit}
                        />
                      </div>
                    </RichTooltip>
              }
            </div>
          </PanelFooter>
        </Form>
      </FormikProvider>
    </>
  )
  const expenseContent = (
    <ExpenseAndRevenueDrawer open={openEditDraw} onClose={closeExpenseEditDrawer} >
      <ExpenseAndRevenueDetailView
        expenseId={drawerType === assignmentTypes.EXPENSE ? expenseId : null}
        revenueId={drawerType === assignmentTypes.REVENUE ? revenueId : null}
        onClose={closeExpenseEditDrawer}
      />
    </ExpenseAndRevenueDrawer>
  )

  return (
    <div className={clsx(classes.container, className)}>
      <Loader customClass={classes.loader} visible={isLoading} text={lang.generics.loading} />
      <EconomicAssetTablePreview
        onClose={onClose}
        economicAssetData={economicAssetData}
        data={calculateData}
        depreciationMethod={method}
        costOfPurchase={costOfPurchase}
        economicLife={economicLifeForTable}
        number={formik.values.number}
        description={formik.values.description}
        dateOfPurchase={formik.values.dateOfPurchase}
        periodType={periodType}
        specialDepreciationData={specialDepreciationData}
        isSpecialDepreciation={isSpecialDepreciation}
        isGWG={method === DEPRECATION_METHODS.lowValueAsset}
        possibleDepreciationMethods={possibleDepreciationMethods}
        isLoading={isLoading || calculationIsLoading}
        onHeaderClick={handleDepreciationMethodChange}
      />
      <Panel className={classes.mainContainer}>
        {content}
        {expenseContent}
        <EconomicAssetBookingModal
          open={openBookingDrawer}
          onClose={() => setOpenBookingDrawer(false)}
          dateOfPurchase={formik.values.dateOfPurchase}
          economicAssetId={economicAssetId}
          closeEconomicAssetDrawer={onClose}
          onRefresh={getEconomicAsset} />
      </Panel>
    </div>
  )
}

EconomicAssetDetailView.propTypes = {
  onClose: PropTypes.func.isRequired,
  economicAssetId: PropTypes.string,
  isExpense: PropTypes.bool,
}

export default EconomicAssetDetailView
